// Hand to AE (spec C6; the Record board's Hand to AE dialog, Dialog and ConfirmPopup cards): a reason,
// the account executive it goes to and an optional note, confirm, then a toast with Undo
// (use-hand-to-ae.tsx). The renewal stays where it is with an Awaiting AE tag until the AE accepts.
// Also the Awaiting AE tag and the Record's waiting banner with Cancel handoff, so every page shows
// them alike.
import { useEffect, useRef, useState } from 'react';
import type { Api, Assignee, HandoffReason, OpenHandoff, RenewalRow } from '../auth/api';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { Dialog } from '../shell/Dialog';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, ClockIcon } from '../shell/icons';
import { Tip } from '../shell/Tip';
import {
  awaitingTag,
  cancelCopy,
  cancelledText,
  handConfirm,
  handFailedText,
  handedText,
  handoffReasonLabels,
  mayCancel,
  otherTextMax,
  waitingText,
} from './handoff-lines';
import { noteMax } from './notes-lines';
import { band, statuses } from './renewal-lines';

export type HandRow = Pick<
  RenewalRow,
  'id' | 'insured' | 'carrier' | 'line' | 'policyNumber' | 'handledBy' | 'status' | 'daysLeft'
>;

export function HandToAeDialog({
  api,
  row,
  preset,
  onClose,
  onDone,
}: {
  api: Api;
  row: HandRow;
  /** A one-click hand's reason and note, filled in and still editable (the Record board). */
  preset?: { reason: HandoffReason; note: string } | null;
  onClose: () => void;
  onDone: (text: string, handoffId: string) => void;
}) {
  const [aes, setAes] = useState<Assignee[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [reason, setReason] = useState<HandoffReason | null>(preset?.reason ?? null);
  const [other, setOther] = useState('');
  const [to, setTo] = useState<Assignee | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [note, setNote] = useState(preset?.note ?? '');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.assignees().then(
      (p) => live && setAes(p.people.filter((x) => x.role === 'account_executive')),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [api]);

  const otherText = other.trim();
  const ready = !!reason && !!to && (reason !== 'other' || otherText.length > 0);
  const subline = [
    row.insured,
    row.carrier ?? 'Carrier not in AMS360',
    row.line,
    row.policyNumber,
  ].join(' | ');
  const status = statuses[row.status];
  const upcoming = band(row.daysLeft) === 'upcoming' && status.stage === 'open';

  // Two fast clicks land before the disabled button renders: only the first sends.
  const sending = useRef(false);
  const submit = async () => {
    if (sending.current || !reason || !to) return;
    sending.current = true;
    setBusy(true);
    setError(null);
    const res = await api
      .createHandoff(row.id, {
        kind: 'handoff',
        reason,
        ...(reason === 'other' ? { otherText } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        to: to.id,
        from: row.handledBy?.id ?? null,
      })
      .finally(() => {
        sending.current = false;
      });
    setBusy(false);
    if (!res.ok) {
      setConfirming(false);
      setError(handFailedText(res.status, res.error));
      return;
    }
    onDone(handedText(row.insured, to.name), res.body.handoff.id);
  };

  return (
    <>
      <Dialog
        id="ae-t"
        title="Hand to AE"
        subline={subline}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary ml-auto"
              disabled={!ready || busy}
              onClick={() => setConfirming(true)}
            >
              Hand to AE
            </button>
          </>
        }
      >
        <div role="radiogroup" aria-labelledby="ae-r">
          <div id="ae-r" className="flab">
            Reason <span className="req">*</span>
          </div>
          <div className="fopts">
            {(Object.keys(handoffReasonLabels) as HandoffReason[]).map((r) => (
              <button
                key={r}
                type="button"
                className={reason === r ? 'chip on' : 'chip'}
                aria-pressed={reason === r}
                onClick={() => setReason(r)}
              >
                {handoffReasonLabels[r]}
              </button>
            ))}
          </div>
          {reason === 'other' && (
            <div style={{ marginTop: 8 }}>
              <label className="flab" htmlFor="ae-other">
                What is it for? <span className="req">*</span>
              </label>
              <input
                id="ae-other"
                className="input"
                placeholder="For example: insured wants an umbrella quote"
                maxLength={otherTextMax}
                value={other}
                onChange={(e) => setOther(e.target.value)}
              />
            </div>
          )}
        </div>
        <div>
          <span className="flab" id="ae-to-l">
            Goes to <span className="req">*</span>
          </span>
          <div className="sel">
            <button
              type="button"
              className={to ? 'sel-b' : 'sel-b ph'}
              id="ae-to"
              aria-haspopup="listbox"
              aria-expanded={listOpen}
              aria-labelledby="ae-to-l ae-to"
              disabled={!aes}
              onClick={() => setListOpen(!listOpen)}
            >
              <span className="sel-v">
                {to
                  ? to.name
                  : aes
                    ? 'Pick an account executive'
                    : failed
                      ? 'Could not load'
                      : 'Loading'}
              </span>
              <ChevronDownIcon className="ico s12" />
            </button>
            {listOpen && aes && (
              <>
                <button
                  type="button"
                  className="pop-scrim"
                  aria-label="Close list"
                  onClick={() => setListOpen(false)}
                />
                <div className="menu sel-m" role="listbox" aria-label="Goes to">
                  {aes.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="mi"
                      role="option"
                      aria-selected={to?.id === p.id}
                      onClick={() => {
                        setTo(p);
                        setListOpen(false);
                      }}
                    >
                      <span className="dd-ck">
                        {to?.id === p.id && <CheckIcon className="ico s12" />}
                      </span>
                      <span className="col g2" style={{ minWidth: 0 }}>
                        <span>{p.name}</span>
                        {p.outToday && <span className="sel-h">Out today</span>}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          {aes && aes.length === 0 && (
            <p className="ferr">No account executive is active yet. Ask the Admin to add one.</p>
          )}
          {failed && (
            <p className="ferr">The list of people could not be loaded. Close and try again.</p>
          )}
        </div>
        <div>
          <label className="flab" htmlFor="ae-note">
            Note <span className="optl">(optional)</span>
          </label>
          <textarea
            id="ae-note"
            className="input"
            placeholder="For example: increase is over the threshold"
            maxLength={noteMax}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <div className="flow">
          <span>After sending</span>
          <span className={`pill ${status.pill}`}>{upcoming ? 'Not started' : status.label}</span>
          <ArrowRightIcon />
          <span className="pill s-awaitae">Awaiting AE</span>
          <span>until the AE accepts</span>
        </div>
        {error && (
          <p className="ferr" role="alert">
            {error}
          </p>
        )}
      </Dialog>
      {confirming && to && (
        <ConfirmPopup
          {...handConfirm(row.insured, to.name)}
          action={busy ? 'Handing to AE' : 'Hand to AE'}
          busy={busy}
          onConfirm={() => void submit()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}

/** The Awaiting AE (or red Overdue) tag with its tooltip; nothing once answered. */
export function AwaitingTag({ h, meId }: { h: OpenHandoff | null | undefined; meId: string }) {
  const tag = awaitingTag(h ?? null, new Date(), meId);
  if (!tag) return null;
  return (
    <Tip text={tag.tip} focusable>
      <span className={tag.cls}>{tag.label}</span>
    </Tip>
  );
}

/**
 * The Record's banner while the handoff waits (the Record board): who it waits on, and Cancel
 * handoff for whoever sent it, the Manager and the Admin, after a confirm.
 */
export function WaitingBanner({
  api,
  h,
  holder,
  me,
  onChanged,
}: {
  api: Api;
  h: OpenHandoff;
  holder: { id: string; name: string } | null;
  me: { id: string; manages: boolean };
  onChanged: (text: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancel = async () => {
    setBusy(true);
    const res = await api.cancelHandoff(h.id);
    setBusy(false);
    setConfirming(false);
    if (!res.ok) {
      setError('Could not cancel it. The AE may have answered it already. Read the Record again.');
      return;
    }
    onChanged(cancelledText(h));
  };
  return (
    <>
      <div className="bnr info" role="status">
        <ClockIcon />
        <span>{error ?? waitingText(h, new Date(), me.id)}</span>
        {mayCancel(h, me.id, me.manages) && (
          <button
            type="button"
            className="btn btn-sm ml-auto"
            aria-haspopup="dialog"
            onClick={() => setConfirming(true)}
          >
            Cancel handoff
          </button>
        )}
      </div>
      {confirming && (
        <ConfirmPopup
          {...cancelCopy(h, holder, me.id)}
          action={busy ? 'Cancelling' : 'Cancel handoff'}
          cancel="Keep it"
          busy={busy}
          onConfirm={() => void cancel()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}
